<template>
  <DialogHeader>
    <div>Ownership</div>
    <Pill v-if="showProCallout" class="ml-2.5" success>Pro only</Pill>
  </DialogHeader>
  <DialogContent class="h-full overflow-y-scroll">
    <template v-if="loading">
      <template>
        <Skeleton class="h-[64px] mb-5" />
        <Skeleton class="h-[48px] mb-5" />
      </template>

      <div
        v-for="n in 3"
        :key="n"
        class="w-full h-[64px] flex items-center first:border-t border-b border-gray-800/8"
      >
        <Skeleton class="w-8 h-8 rounded-full mr-2.5" />
        <div>
          <Skeleton class="w-[200px] h-[14px] mb-1.5" />
          <Skeleton class="w-[140px] h-[12px]" />
        </div>
      </div>
    </template>

    <template v-else>
      <UpgradeToProCallout v-if="showProCallout" class="mb-5">
        Enable privacy/invite controls and other features
      </UpgradeToProCallout>

      <div :class="classes">
        <FileInfo :file="props.file" class="mb-5">
          Owner(s) of the original work
        </FileInfo>

        <SearchMultipleUsers
          v-model="usersToInvite"
          :disabled="isBasicUser"
          :show-own-entity="true"
          class="mb-2.5"
        />

        <UiButton
          class="ml-auto mb-5"
          :disabled="!usersToInvite.length"
          :loading="sendInvitesLoading"
          primary
          @click="handleInviteClick"
        >
          Send invite
        </UiButton>
      </div>

      <div class="mb-6">
        <div class="w-full p-[14px] bg-gray-200/30 rounded-md flex">
          <Avatar
            :src="props.file.owner.avatar.url"
            :size="32"
            class="mr-2.5"
          />
          <div class="mt-0.5">
            <div class="flex items-start justify-between mb-[15px]">
              <div>
                <div>{{ props.file.owner.name }}</div>
                <div
                  v-if="getOwnerSubtext"
                  class="text-[13px] leading-[18px] text-gray-800/50"
                >
                  {{ getOwnerSubtext }}
                </div>
              </div>
              <div class="flex items-center">
                <Icon
                  v-if="props.file.owner.__typename === 'Organization'"
                  name="Organization"
                  :size="18"
                  class="mr-2.5 stroke-gray-500"
                />
                <Pill>Owner</Pill>
              </div>
            </div>

            <div>
              <span class="font-[570]">Principle copyright owner</span>. You
              must sell or reassign ownership of the copyright to change this
              owner.
            </div>
          </div>
        </div>
      </div>

      <Table.Base class="flex flex-col border-t border-gray-800/8">
        <Table.Row
          v-for="item in items"
          :key="getKey(item)"
          class="hover:bg-transparent! cursor-default!"
        >
          <Table.Cell class="h-[64px]!">
            <Avatar
              v-if="isEntity(item)"
              :size="32"
              :src="getEntityAvatar(item)"
              class="mr-2.5"
            />
            <div
              v-else
              class="w-8 h-8 flex items-center justify-center mr-2.5 opacity-50"
            >
              <Icon name="Send" :size="22" class="stroke-gray-800" />
            </div>
            <div>
              <div :class="getNameClass(item)">
                {{ getEntityNameOrEmail(item) }}
              </div>
              <div class="text-[13px] leading-[18px] text-gray-800/50">
                <template v-if="isUser(item)">
                  {{ (getEntityData(item) as UserType).username }}
                </template>
                <template v-if="isOrganization(item)">
                  Managed by {{ getOrgOwnerName(item) }}
                </template>
              </div>
            </div>
          </Table.Cell>
          <Table.Cell class="justify-end h-[64px]!">
            <div
              v-if="isInvited(item)"
              class="font-primary text-[13px] leading-[20px] opacity-50"
            >
              Invited
            </div>
            <div v-else class="flex items-center gap-x-1.5">
              <Pill v-for="pill in getPills(item)" :key="pill">
                {{ pill }}
              </Pill>
            </div>
            <Float.Base v-if="showThreeDots(item)" class="ml-[14px]">
              <Float.Trigger>
                <div
                  class="w-[30px] h-[30px] rounded-sm hover:bg-gray-800/6 flex items-center justify-center cursor-pointer"
                >
                  <Icon
                    name="ThreeDots"
                    :size="18"
                    class="stroke-gray-800 fill-gray-800"
                  />
                </div>
              </Float.Trigger>
              <Float.Component>
                <MenuList>
                  <MenuListItem @click="handleRemoveClick(item)">
                    <Icon
                      name="Delete"
                      :size="18"
                      class="stroke-red-700 mr-3"
                    />
                    <div class="text-red-700">Remove</div>
                  </MenuListItem>
                </MenuList>
              </Float.Component>
            </Float.Base>
          </Table.Cell>
        </Table.Row>
      </Table.Base>
    </template>
  </DialogContent>
</template>

<script setup lang="ts">
import DialogHeader from '@/shared/dialogs/DialogHeader.vue'
import DialogContent from '@/shared/dialogs/DialogContent.vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'
import { graphql } from '@/shared/graphql/router'
import {
  FileRole,
  InviteType,
  type File,
  type User,
  type Organization,
  type CoOwnersConnection,
} from '@/shared/graphql/router/graphql'
import {
  UserPermission,
  type Entity as LegacyEntity,
  type BasicUser as LegacyBasicUser,
  type Organization as LegacyOrganization,
} from '@/shared/graphql/legacy/generated'
import { useQuery, useMutation } from '@vue/apollo-composable'
import { Avatar, Pill, UiButton, Skeleton, Icon, Float, Table } from '@dacr/ui'
import { MenuList, MenuListItem } from '@/shared/components/Menu'
import { useUserStore } from '@/shared/stores/user'
import { computed, ref } from 'vue'
import FileInfo from '@/features/explorer/components/FileInfo.vue'
import SearchMultipleUsers from '@/features/search/components/SearchMultipleUsers.vue'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import UpgradeToProCallout from '@/features/billing/components/UpgradeToProCallout.vue'

type UserType = User
type OrgType = Organization

const FILE_OWNERS_QUERY = graphql(`
  query FileOwners($id: ID!) {
    file(id: $id) {
      id
      coOwners(input: { first: 100, filterBy: ALL }) {
        totalCount
        edges {
          node {
            ... on EntityWithRole {
              role
              entity {
                ... on User {
                  id
                  name
                  username
                  avatar {
                    url
                  }
                }
                ... on Organization {
                  id
                  name
                  avatar {
                    url
                  }
                  owner {
                    ... on User {
                      id
                      name
                    }
                  }
                }
              }
            }
            ... on EmailInvite {
              email
            }
          }
        }
      }
    }
  }
`)

const ADD_CO_OWNERS_MUTATION = graphql(`
  mutation AddCoOwners($input: AddCoOwnersInput!) {
    addCoOwners(input: $input) {
      error {
        code
        message
      }
      file {
        id
        coOwners(input: { first: 100, filterBy: ALL }) {
          totalCount
          edges {
            node {
              ... on EntityWithRole {
                role
                entity {
                  ... on User {
                    id
                    name
                    username
                    avatar {
                      url
                    }
                  }
                  ... on Organization {
                    id
                    name
                    avatar {
                      url
                    }
                    owner {
                      ... on User {
                        id
                        name
                      }
                    }
                  }
                }
              }
              ... on EmailInvite {
                email
              }
            }
          }
        }
      }
    }
  }
`)

const { close } = useDialog()
const { open, triggerEvent } = useDialog()

const props = defineProps<{
  file: File
}>()

const { result, loading } = useQuery(
  FILE_OWNERS_QUERY,
  { id: props.file.copyright.id || '' },
  {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-only',
    clientId: 'router',
  }
)

const { mutate: sendInvites, loading: sendInvitesLoading } = useMutation(
  ADD_CO_OWNERS_MUTATION,
  { clientId: 'router' }
)

const toastStore = useToastStore()
const userStore = useUserStore()

const usersToInvite = ref<LegacyEntity[]>([])

const isBasicUser = computed(
  () => !userStore.user?.permissions.includes(UserPermission.Pro)
)

const isOwner = computed(() => props.file.role === FileRole.Owner)

const showProCallout = computed(() => isBasicUser.value && isOwner.value)

type CoOwnerNode = NonNullable<
  NonNullable<typeof result.value>['file']
>['coOwners']['edges'][number]['node']

const items = computed(
  () => result.value?.file?.coOwners.edges.map((e) => e.node) ?? []
)

const classes = computed(() => ({
  'opacity-30 pointer-events-none': isBasicUser.value,
}))

const getNameClass = (item: CoOwnerNode) => ({
  'text-[13px] opacity-50': item.__typename === 'EmailInvite',
})

type CoOwnerEmailInvite = Extract<CoOwnerNode, { email: string }>
type CoOwnerEntityNode = Exclude<CoOwnerNode, CoOwnerEmailInvite>

const isEmailInvite = (item: CoOwnerNode): item is CoOwnerEmailInvite =>
  'email' in item

const asEntity = (item: CoOwnerNode) => item as CoOwnerEntityNode

const getKey = (item: CoOwnerNode) =>
  isEmailInvite(item) ? item.email : asEntity(item).entity.id

const isEntity = (item: CoOwnerNode) => !isEmailInvite(item)

const getEntityData = (item: CoOwnerNode) =>
  isEmailInvite(item) ? null : asEntity(item).entity

const getEntityAvatar = (item: CoOwnerNode) =>
  isEmailInvite(item) ? undefined : asEntity(item).entity.avatar.url

const getEntityNameOrEmail = (item: CoOwnerNode) =>
  isEmailInvite(item) ? item.email : asEntity(item).entity.name

const getOwnerSubtext = computed(() => {
  const owner = props.file.owner
  if (owner.__typename === 'User') {
    return 'username' in owner ? (owner as UserType).username : undefined
  }
  if (owner.__typename === 'Organization') {
    const org = owner as OrgType
    const orgOwner = org.owner
    if (orgOwner && 'name' in orgOwner) return `Managed by ${orgOwner.name}`
  }
  return undefined
})

const getInviteType = (user: LegacyEntity) => {
  if (user.id === user.name) return InviteType.Email
  return user.__typename === 'BasicUser' ? InviteType.User : InviteType.Org
}

const showThreeDots = (item: CoOwnerNode) => {
  if (isEmailInvite(item)) return isOwner.value
  const e = asEntity(item)
  if (e.role === FileRole.Owner) return false
  if (isOwner.value) return true
  if (e.entity.__typename === 'Organization') {
    const orgOwner = (e.entity as OrgType).owner
    return orgOwner?.__typename === 'User' && orgOwner.id === userStore.user?.id
  }
  return e.entity.id === userStore.user?.id
}

const isInvited = (item: CoOwnerNode) =>
  isEmailInvite(item) || asEntity(item).role === FileRole.Invited

const getPills = (item: CoOwnerNode) => {
  if (isEmailInvite(item)) return []
  const e = asEntity(item)
  if (e.role === FileRole.Owner) return ['File admin']
  if (e.role === FileRole.CoOwner) return ['Owner']
  return []
}

const isUser = (item: CoOwnerNode) =>
  !isEmailInvite(item) && asEntity(item).entity.__typename === 'User'

const isOrganization = (item: CoOwnerNode) =>
  !isEmailInvite(item) && asEntity(item).entity.__typename === 'Organization'

const getOrgOwnerName = (item: CoOwnerNode) => {
  if (isEmailInvite(item)) return ''
  const org = asEntity(item).entity as OrgType
  return org.owner?.__typename === 'User' ? org.owner.name : ''
}

const handleRemoveClick = (item: CoOwnerNode) => {
  document.body.click()

  let inviteType: InviteType
  let invitePayload: { id?: string; email?: string }

  if (isEmailInvite(item)) {
    inviteType = InviteType.Email
    invitePayload = { email: item.email }
  } else {
    const e = asEntity(item)
    inviteType =
      e.entity.__typename === 'User' ? InviteType.User : InviteType.Org
    invitePayload = { id: e.entity.id }
  }

  open(Dialogs.RemoveFileOwner, {
    props: {
      fileId: props.file.id,
      invite: {
        ...invitePayload,
        type: inviteType,
      },
      name: getEntityNameOrEmail(item),
    },
    events: {
      success() {
        triggerEvent('success', {
          ...props.file,
        })
      },
    },
  })
}

const buildOptimisticEdge = (user: LegacyEntity) => {
  if (getInviteType(user) === InviteType.Email) {
    return {
      __typename: 'CoOwnerEdge' as const,
      cursor: '',
      node: { __typename: 'EmailInvite' as const, email: user.id },
    }
  }

  const isUser = user.__typename === 'BasicUser'
  return {
    __typename: 'CoOwnerEdge' as const,
    cursor: '',
    node: {
      __typename: 'EntityWithRole' as const,
      role: FileRole.Invited,
      entity: isUser
        ? {
            __typename: 'User' as const,
            id: user.id,
            name: user.name,
            username: (user as LegacyBasicUser).username,
            avatar: {
              __typename: 'Image' as const,
              url: (user as LegacyBasicUser).avatarUrl,
            },
          }
        : {
            __typename: 'Organization' as const,
            id: user.id,
            name: user.name,
            avatar: {
              __typename: 'Image' as const,
              url: (user as LegacyOrganization).avatarUrl,
            },
            owner: (user as LegacyOrganization).owner
              ? {
                  __typename: 'User' as const,
                  id: (user as LegacyOrganization).owner!.id,
                  name: (user as LegacyOrganization).owner!.name,
                }
              : { __typename: 'Organization' as const },
          },
    },
  }
}

const handleInviteClick = async () => {
  const invitedUsers = [...usersToInvite.value]

  const invites = invitedUsers.map((user) => ({
    ...(getInviteType(user) === InviteType.Email
      ? { email: user.id }
      : { id: user.id }),
    type: getInviteType(user),
  }))

  const currentEdges = result.value?.file?.coOwners.edges ?? []
  const currentCount = result.value?.file?.coOwners.totalCount ?? 0

  try {
    await sendInvites(
      { input: { fileId: props.file.id, invites } },
      {
        update(cache) {
          cache.modify({
            id: cache.identify({ __typename: 'File', id: props.file.id }),
            fields: {
              coOwners(existing, { storeFieldName }) {
                if (!storeFieldName.includes('ALL')) return existing
                const data = existing as CoOwnersConnection
                if (!data?.edges) return existing

                return {
                  ...existing,
                  totalCount: currentCount + invitedUsers.length,
                  edges: [
                    ...currentEdges,
                    ...invitedUsers.map(buildOptimisticEdge),
                  ],
                }
              },
            },
          })
        },
      }
    )

    usersToInvite.value = []

    toastStore.showToast({
      type: ToastType.Success,
      content: 'Invites were successfully sent',
    })
  } catch (error) {
    console.error(error)

    toastStore.showToast({
      type: ToastType.Error,
      content: 'Could not send invites - please try again later',
      error: error as Error,
    })
  }
}
</script>
